import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import { effectiveHomePath } from 'model/userSession';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faFolder,
  faRotateRight,
  faStop,
} from '@fortawesome/free-solid-svg-icons';
import { desktopAppTaskContex } from 'screen/desktop';
import { resolveEntryIcon } from 'screen/apps/fileBrowser/iconHandler';
import { beginTransfer, endTransfer } from 'service/sessionActivity';
import { DsEntry } from 'service/apiClient';
import fileUploaderApi, {
  getUploadDrop,
  notifyDsChanged,
  releaseUploadDrop,
  type StashedDrop,
} from './api';
import './fileUploader.css';

export const fileUploaderConfig = {
  appID: 'fileUploader',
  showName: 'Upload',
  onList: false,
  icon: require('./images/fileUploader.png'),
  maxHead: 8,
  defaultClosable: false,
  req: {
    minSize: { width: 380, height: 320 },
    defSize: { width: 460, height: 520 },
  },
};

const WATERMARK = require('../../../image/upload-watermark.png');
const JUNK_FILE = /^(?:\.DS_Store|Thumbs\.db|desktop\.ini)$/i;

type DropFile = { file: File; rel: string };
type UpItem = {
  rel: string;
  name: string;
  pct: number;
  err?: boolean;
  stopped?: boolean;
  dir?: boolean;
  file?: File;
};

function isCanceledError(e: any) {
  return e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
}
type Phase = 'pick' | 'prep' | 'run' | 'done';

function basename(path: string): string {
  const cleaned = path.replace(/\/+$/, '');
  const idx = cleaned.lastIndexOf('/');
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned;
}

function parentPath(path: string): string | null {
  const cleaned = path.replace(/\/+$/, '') || '/';
  if (cleaned === '/' || cleaned === '') {
    return null;
  }
  const idx = cleaned.lastIndexOf('/');
  if (idx <= 0) {
    return '/';
  }
  return cleaned.slice(0, idx);
}

function joinPath(dir: string, name: string): string {
  if (dir.endsWith('/')) {
    return dir + name;
  }
  return dir + '/' + name;
}

function uniqueDestPath(dir: string, name: string, existing: Set<string>): string {
  let candidate = joinPath(dir, name);
  if (!existing.has(candidate)) {
    return candidate;
  }
  const dot = name.lastIndexOf('.');
  const hasExt = dot > 0 && !name.startsWith('.');
  const stem = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : '';
  let n = 1;
  while (true) {
    const nextName = `${stem} (copy${n > 1 ? ' ' + n : ''})${ext}`;
    candidate = joinPath(dir, nextName);
    if (!existing.has(candidate)) {
      return candidate;
    }
    n += 1;
  }
}

function remapRel(rel: string, alias: Map<string, string>): string {
  const i = rel.indexOf('/');
  const root = i < 0 ? rel : rel.slice(0, i);
  return (alias.get(root) || root) + (i < 0 ? '' : rel.slice(i));
}

function readDirEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const tick = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(all);
          return;
        }
        all.push(...batch);
        tick();
      }, reject);
    };
    tick();
  });
}

async function collectDrop(drop: StashedDrop): Promise<{ files: DropFile[]; dirs: string[] }> {
  const files: DropFile[] = [];
  const dirs = new Set<string>();
  const walk = async (entry: FileSystemEntry, prefix: string) => {
    const rel = prefix + entry.name;
    if (entry.isDirectory) {
      dirs.add(rel);
      const kids = await readDirEntries((entry as FileSystemDirectoryEntry).createReader());
      for (const kid of kids) {
        await walk(kid, `${rel}/`);
      }
      return;
    }
    if (!JUNK_FILE.test(entry.name)) {
      const file = await new Promise<File>((res, rej) =>
        (entry as FileSystemFileEntry).file(res, rej)
      );
      files.push({ file, rel });
    }
  };
  for (const entry of drop.entries) {
    await walk(entry, '');
  }
  if (drop.entries.length === 0) {
    drop.files.forEach((file) => {
      if (JUNK_FILE.test(file.name)) {
        return;
      }
      const rel = file.webkitRelativePath || file.name;
      files.push({ file, rel: rel.replace(/\\/g, '/') });
    });
  }
  for (const f of files) {
    const parts = f.rel.split('/');
    for (let i = 1; i < parts.length; i += 1) {
      dirs.add(parts.slice(0, i).join('/'));
    }
  }
  return { files, dirs: Array.from(dirs) };
}

const UpRow = React.memo(function UpRow(props: {
  u: UpItem;
  canRetry: boolean;
  onRetry: (rel: string) => void;
}) {
  const { u, canRetry, onRetry } = props;
  return (
    <div
      className={`fu-row${u.stopped ? ' stopped' : u.err ? ' err' : ''}${!u.err && !u.stopped && u.pct >= 100 ? ' done' : ''}`}
      data-up-rel={u.rel}
      title={u.rel}
    >
      <div className="fu-ico">
        <img src={resolveEntryIcon(u.name, !!u.dir)} alt="" draggable={false} />
      </div>
      <div className="fu-name">{u.rel}</div>
      <div className="fu-meta">
        <span>{u.stopped ? 'Stopped' : u.err ? 'Failed' : `${u.pct}%`}</span>
        {(u.err || u.stopped) && canRetry && u.file && (
          <button
            type="button"
            className="fu-retry"
            title="Retry"
            onClick={() => onRetry(u.rel)}
          >
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
        )}
      </div>
      <div className="fu-bar">
        <i style={{ width: `${u.err ? 100 : u.pct}%` }} />
      </div>
    </div>
  );
});

function parseSessionId(parms?: TaskInitParams[]): string {
  if (!parms) {
    return '';
  }
  const found = parms.find((p) => p.command === 'session' && p.value);
  return found?.value || '';
}

function FileUploader(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const listRef = useRef<HTMLDivElement>(null);
  const uploadsRef = useRef<UpItem[]>([]);
  const destRef = useRef('');
  const upRafRef = useRef(0);
  const busyRef = useRef(false);
  const sessionIdRef = useRef('');
  const cancelledRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const dropRef = useRef<StashedDrop | null>(null);
  const pickGenRef = useRef(0);

  const [destPath, setDestPath] = useState('');
  const [phase, setPhase] = useState<Phase>('prep');
  const [uploads, setUploads] = useState<UpItem[]>([]);
  const [activeRel, setActiveRel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [incomingNames, setIncomingNames] = useState<string[]>([]);
  const [pickPath, setPickPath] = useState('/');
  const [pickFolders, setPickFolders] = useState<DsEntry[]>([]);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);

  const setClosable = useCallback(
    (closable: boolean) => {
      if (props.taskID) {
        desktopControll?.setTaskClosable?.(props.taskID, closable);
      }
    },
    [desktopControll, props.taskID]
  );

  const flushUploads = () => {
    upRafRef.current = 0;
    setUploads(uploadsRef.current.slice());
  };

  const bumpUpload = (rel: string, patch: Partial<UpItem>) => {
    const items = uploadsRef.current;
    const i = items.findIndex((u) => u.rel === rel);
    if (i < 0) {
      return;
    }
    const prev = items[i];
    if (patch.pct != null && patch.err === undefined && prev.pct === patch.pct && !prev.err) {
      return;
    }
    items[i] = { ...prev, ...patch };
    if (!upRafRef.current) {
      upRafRef.current = requestAnimationFrame(flushUploads);
    }
  };

  const scrollToRel = (rel: string) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const root = listRef.current;
        if (!root) {
          return;
        }
        const tiles = root.children;
        for (let i = 0; i < tiles.length; i += 1) {
          const el = tiles[i] as HTMLElement;
          if (el.dataset.upRel === rel) {
            const er = el.getBoundingClientRect();
            const rr = root.getBoundingClientRect();
            root.scrollTop += er.top - rr.top - (root.clientHeight - er.height) / 2;
            const winBody = root.closest('.windowBody');
            if (winBody instanceof HTMLElement) {
              winBody.scrollTop = 0;
            }
            return;
          }
        }
      });
    });
  };

  const putFile = async (
    rel: string,
    file: File,
    dest: string,
    isCancelled?: () => boolean
  ) => {
    if (isCancelled?.()) {
      return;
    }
    bumpUpload(rel, { err: false, stopped: false, pct: 0 });
    setActiveRel(rel);
    if (upRafRef.current) {
      cancelAnimationFrame(upRafRef.current);
    }
    flushUploads();
    scrollToRel(rel);
    try {
      await fileUploaderApi.uploadFile(joinPath(dest, rel), file, {
        signal: abortRef.current?.signal,
        onProgress: (loaded, total) => {
          if (isCancelled?.()) {
            return;
          }
          const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          bumpUpload(rel, { pct });
        },
      });
      if (isCancelled?.()) {
        return;
      }
      bumpUpload(rel, { pct: 100, err: false, stopped: false });
    } catch (e: any) {
      if (isCancelled?.() || isCanceledError(e)) {
        bumpUpload(rel, { err: true, stopped: true });
        return;
      }
      bumpUpload(rel, { err: true, stopped: false });
    }
  };

  const finishBatch = (dest: string) => {
    if (upRafRef.current) {
      cancelAnimationFrame(upRafRef.current);
      flushUploads();
    }
    setActiveRel(null);
    setPhase('done');
    const wasBusy = busyRef.current;
    busyRef.current = false;
    setClosable(true);
    if (wasBusy) {
      endTransfer();
    }
    if (dest) {
      notifyDsChanged(dest);
    }
    if (sessionIdRef.current) {
      releaseUploadDrop(sessionIdRef.current);
      sessionIdRef.current = '';
    }
  };

  const markUnfinishedStopped = () => {
    let changed = false;
    uploadsRef.current = uploadsRef.current.map((u) => {
      if (u.err || u.stopped || u.pct >= 100) {
        return u;
      }
      changed = true;
      return { ...u, err: true, stopped: true };
    });
    if (changed) {
      if (upRafRef.current) {
        cancelAnimationFrame(upRafRef.current);
      }
      flushUploads();
    }
  };

  const stopUpload = () => {
    if (!busyRef.current) {
      return;
    }
    cancelledRef.current = true;
    abortRef.current?.abort();
    markUnfinishedStopped();
    if (uploadsRef.current.length === 0) {
      setError('Upload stopped.');
    }
    finishBatch(destRef.current);
  };

  const runItems = async (
    dest: string,
    rels: string[],
    isCancelled?: () => boolean
  ) => {
    for (const rel of rels) {
      if (isCancelled?.()) {
        return;
      }
      const item = uploadsRef.current.find((u) => u.rel === rel);
      if (!item?.file) {
        continue;
      }
      await putFile(rel, item.file, dest, isCancelled);
    }
  };

  const startUploadTo = useCallback(
    async (dest: string, existingPaths: string[]) => {
      const drop = dropRef.current;
      if (!drop) {
        setError('Nothing to upload.');
        setPhase('done');
        setClosable(true);
        return;
      }
      destRef.current = dest;
      setDestPath(dest);
      if (props.taskID) {
        desktopControll?.setTaskTitle?.(props.taskID, dest);
      }
      setPhase('prep');
      setClosable(false);
      setError(null);
      cancelledRef.current = false;
      abortRef.current = new AbortController();
      busyRef.current = true;
      beginTransfer();
      const isCancelled = () => cancelledRef.current;
      try {
        const collected = await collectDrop(drop);
        if (isCancelled()) {
          return;
        }
        if (collected.files.length === 0 && collected.dirs.length === 0) {
          setError('Nothing to upload.');
          finishBatch(dest);
          return;
        }
        const taken = new Set(existingPaths);
        const roots = new Set<string>();
        collected.files.forEach((f) => roots.add(f.rel.split('/')[0]));
        collected.dirs.forEach((d) => roots.add(d.split('/')[0]));
        const alias = new Map<string, string>();
        roots.forEach((root) => {
          const next = uniqueDestPath(dest, root, taken);
          taken.add(next);
          alias.set(root, basename(next));
        });
        const files = collected.files.map((f) => ({ ...f, rel: remapRel(f.rel, alias) }));
        const dirs = collected.dirs
          .map((d) => remapRel(d, alias))
          .sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
        const items: UpItem[] = files.length
          ? files.map((f) => ({ rel: f.rel, name: basename(f.rel), pct: 0, file: f.file }))
          : dirs.map((rel) => ({ rel, name: basename(rel), pct: 0, dir: true }));
        if (isCancelled()) {
          return;
        }
        uploadsRef.current = items;
        setUploads(items);
        setPhase('run');
        if (sessionIdRef.current) {
          releaseUploadDrop(sessionIdRef.current);
          sessionIdRef.current = '';
        }
        for (const dir of dirs) {
          if (isCancelled()) {
            return;
          }
          try {
            await fileUploaderApi.dsMkdir(joinPath(dest, dir));
          } catch {
            /* exists */
          }
        }
        if (files.length === 0) {
          dirs.forEach((d) => bumpUpload(d, { pct: 100 }));
        } else {
          await runItems(dest, files.map((f) => f.rel), isCancelled);
        }
        if (isCancelled()) {
          return;
        }
        finishBatch(dest);
      } catch (e: any) {
        if (!isCancelled()) {
          setError(e?.message || 'Upload failed');
          finishBatch(dest);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [desktopControll, props.taskID, setClosable]
  );

  const loadPickFolders = useCallback(async (path: string) => {
    const gen = ++pickGenRef.current;
    setPickLoading(true);
    setPickError(null);
    setPickPath(path);
    try {
      const res = await fileUploaderApi.dsLs(path);
      if (gen !== pickGenRef.current) {
        return;
      }
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to list folder');
      }
      const folders = (res.data.data || [])
        .filter((e) => e.directory)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      setPickFolders(folders);
    } catch (e: any) {
      if (gen !== pickGenRef.current) {
        return;
      }
      setPickFolders([]);
      setPickError(e?.response?.data?.message || e?.message || 'Failed to list folder');
    } finally {
      if (gen === pickGenRef.current) {
        setPickLoading(false);
      }
    }
  }, []);

  const confirmPick = useCallback(async () => {
    if (pickPath === '/' || pickLoading) {
      return;
    }
    setClosable(false);
    setPhase('prep');
    setError(null);
    try {
      const res = await fileUploaderApi.dsLs(pickPath);
      const entries = res.data?.success ? res.data.data || [] : [];
      const existing = entries.map((e) => e.path);
      await startUploadTo(pickPath, existing);
    } catch (e: any) {
      setError(e?.message || 'Failed to open folder');
      setPhase('pick');
      setClosable(true);
    }
  }, [pickPath, pickLoading, setClosable, startUploadTo]);

  useEffect(() => {
    cancelledRef.current = false;
    abortRef.current = new AbortController();
    const sessionId = parseSessionId(props.taskInitParms);
    const drop = sessionId ? getUploadDrop(sessionId) : null;
    const hasFiles =
      !!drop &&
      ((drop.files && drop.files.length > 0) || (drop.entries && drop.entries.length > 0));
    if (!drop || (!drop.destPath && !hasFiles)) {
      setError('Nothing to upload.');
      setPhase('done');
      setClosable(true);
      return;
    }
    sessionIdRef.current = sessionId;
    dropRef.current = drop;
    const names =
      drop.files?.map((f) => f.name).filter(Boolean) ||
      drop.entries?.map((e) => e.name).filter(Boolean) ||
      [];
    setIncomingNames(names);

    if (!drop.destPath) {
      const home = effectiveHomePath(desktopControll?.sessionUser);
      setPhase('pick');
      setClosable(true);
      void loadPickFolders(home || '/');
      return () => {
        cancelledRef.current = true;
        if (sessionIdRef.current) {
          releaseUploadDrop(sessionIdRef.current);
          sessionIdRef.current = '';
        }
      };
    }

    void startUploadTo(drop.destPath, drop.existingPaths || []);
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
      if (busyRef.current) {
        busyRef.current = false;
        endTransfer();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryRels = useCallback(
    (rels: string[]) => {
      const dest = destRef.current;
      if (!dest || busyRef.current || rels.length === 0) {
        return;
      }
      const ready = rels.filter((rel) => {
        const item = uploadsRef.current.find((u) => u.rel === rel);
        return !!(item?.file && (item.err || item.stopped));
      });
      if (ready.length === 0) {
        return;
      }
      cancelledRef.current = false;
      abortRef.current = new AbortController();
      busyRef.current = true;
      setPhase('run');
      setClosable(false);
      setError(null);
      beginTransfer();
      void (async () => {
        await runItems(dest, ready, () => cancelledRef.current);
        if (cancelledRef.current) {
          return;
        }
        finishBatch(dest);
      })();
    },
    [setClosable]
  );

  const retryOne = useCallback(
    (rel: string) => {
      retryRels([rel]);
    },
    [retryRels]
  );

  const failed = useMemo(() => uploads.filter((u) => u.err || u.stopped), [uploads]);
  const stoppedCount = uploads.reduce((n, u) => n + (u.stopped ? 1 : 0), 0);
  const okCount = uploads.reduce((n, u) => n + (!u.err && !u.stopped && u.pct >= 100 ? 1 : 0), 0);
  const shown = useMemo(() => {
    if (phase !== 'done') {
      return uploads;
    }
    return [...failed, ...uploads.filter((u) => !u.err && !u.stopped)];
  }, [uploads, failed, phase]);

  const parent = parentPath(pickPath);
  const canUploadHere = phase === 'pick' && pickPath !== '/' && !pickLoading;
  const busy = phase === 'prep' || phase === 'run';
  const title =
    phase === 'pick'
      ? 'Choose folder'
      : phase === 'prep'
        ? 'Preparing'
        : phase === 'run'
          ? 'Uploading'
          : stoppedCount
            ? 'Stopped'
            : failed.length
              ? 'Finished'
              : 'Done';
  const nowItem = activeRel ? uploads.find((u) => u.rel === activeRel) : null;
  const incomingLabel =
    incomingNames.length === 0
      ? ''
      : incomingNames.length <= 3
        ? incomingNames.join(', ')
        : `${incomingNames.slice(0, 2).join(', ')} +${incomingNames.length - 2} more`;

  if (phase === 'pick') {
    return (
      <div className="fu-root">
        <div className="fu-head">
          <div className="fu-kicker">
            <div className="fu-title">{title}</div>
            <div className="fu-sum">{incomingNames.length} file(s)</div>
          </div>
          {incomingLabel ? (
            <div className="fu-now" title={incomingNames.join(', ')}>
              {incomingLabel}
            </div>
          ) : null}
          <code className="fu-path" title={pickPath}>
            {pickPath}
          </code>
        </div>
        <div className="fu-pick-nav">
          <button
            type="button"
            className="fu-nav-btn"
            disabled={!parent || pickLoading}
            onClick={() => parent && void loadPickFolders(parent)}
            title="Up"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Up
          </button>
        </div>
        <div className="fu-list-wrap">
          <div className="fu-bg" style={{ backgroundImage: `url(${WATERMARK})` }} />
          <div className="fu-list" ref={listRef}>
            {pickLoading && <div className="fu-empty">Loading…</div>}
            {!pickLoading && pickError && <div className="fu-empty">{pickError}</div>}
            {!pickLoading && !pickError && pickFolders.length === 0 && (
              <div className="fu-empty">
                {pickPath === '/'
                  ? 'Open a share, then a folder to upload into.'
                  : 'No subfolders here. You can upload into this folder.'}
              </div>
            )}
            {!pickLoading &&
              pickFolders.map((folder) => (
                <button
                  key={folder.path}
                  type="button"
                  className="fu-folder-row"
                  onClick={() => void loadPickFolders(folder.path)}
                >
                  <span className="fu-ico">
                    <img src={resolveEntryIcon(folder.name, true)} alt="" draggable={false} />
                  </span>
                  <span className="fu-name">{folder.name}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="fu-folder-chevron" />
                </button>
              ))}
          </div>
        </div>
        <div className="fu-foot">
          <button
            type="button"
            className="fu-retry-all"
            disabled={!canUploadHere}
            onClick={() => void confirmPick()}
            title={pickPath === '/' ? 'Pick a folder inside a share' : 'Upload here'}
          >
            <FontAwesomeIcon icon={faFolder} />
            {pickPath === '/' ? 'Select a folder' : 'Upload here'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fu-root">
      <div className="fu-head">
        <div className="fu-kicker">
          <div className={`fu-title${busy ? ' busy' : ''}`}>{title}</div>
          <div className="fu-sum">
            {okCount} ok · {failed.length} failed
          </div>
        </div>
        {destPath ? (
          <code className="fu-path" title={destPath}>
            {destPath}
          </code>
        ) : null}
        {nowItem && (
          <div className="fu-now" title={nowItem.rel}>
            {nowItem.rel}
          </div>
        )}
      </div>
      <div className="fu-list-wrap">
        <div className="fu-bg" style={{ backgroundImage: `url(${WATERMARK})` }} />
        <div className="fu-list" ref={listRef}>
          {shown.length === 0 && (
            <div className="fu-empty">
              {error || (phase === 'prep' ? 'Reading dropped files…' : 'Nothing to upload.')}
            </div>
          )}
          {shown.map((u) => (
            <UpRow key={u.rel} u={u} canRetry={!busy} onRetry={retryOne} />
          ))}
          {error && shown.length > 0 && <div className="fu-note">{error}</div>}
        </div>
      </div>
      {busy && (
        <div className="fu-foot">
          <button type="button" className="fu-stop" onClick={stopUpload}>
            <FontAwesomeIcon icon={faStop} />
            Stop upload
          </button>
        </div>
      )}
      {phase === 'done' && failed.length > 0 && (
        <div className="fu-foot">
          <button
            type="button"
            className="fu-retry-all"
            onClick={() => retryRels(failed.map((u) => u.rel))}
          >
            Retry all ({failed.length})
          </button>
        </div>
      )}
    </div>
  );
}

const App: AppObj = {
  config: fileUploaderConfig,
  body: FileUploader,
};

export default App;
